import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeftRight, ChevronDown } from 'lucide-react';
import { useEscapeToClose } from '../hooks/useEscapeToClose';

interface User {
  customerId?: string;
  customerName?: string;
}

interface LinkedCustomer {
  customerNo?: string;
  customerId?: string;
  customerName?: string;
}

interface CustomerSwitcherProps {
  currentUser?: User;
  linkedCustomers: LinkedCustomer[] | null;
  onCustomerSwitch: (customer: LinkedCustomer) => Promise<void>;
  /** Use dark variant when placed inside the app header (white text on dark bg) */
  dark?: boolean;
}

const CustomerSwitcher: React.FC<CustomerSwitcherProps> = ({ currentUser, linkedCustomers, onCustomerSwitch, dark }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEscapeToClose(isOpen, () => setIsOpen(false), dropdownRef);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (!linkedCustomers || linkedCustomers.length <= 1) return null;

  const activeNo = currentUser?.customerId;
  const activeName = currentUser?.customerName;

  const handleSwitch = async (customer: LinkedCustomer): Promise<void> => {
    const customerNo = customer.customerNo || customer.customerId;
    if (customerNo === activeNo) {
      setIsOpen(false);
      return;
    }

    setIsSwitching(true);
    try {
      await onCustomerSwitch(customer);
      setIsOpen(false);
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        disabled={isSwitching}
        className={dark
          ? 'flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white border border-white/10 px-3 py-2 rounded-md cursor-pointer transition-colors text-sm font-medium disabled:opacity-50'
          : 'flex items-center gap-1.5 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm font-medium disabled:opacity-50'}
        title="Switch customer account"
      >
        <ArrowLeftRight className="h-4 w-4 shrink-0" />
        <span className="hidden sm:inline max-w-[280px] truncate" data-sensitive>
          {isSwitching ? 'Switching...' : (activeName || activeNo || 'Select Customer')}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-64 max-w-[calc(100vw-1rem)] bg-white dark:bg-card rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border-b text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Customer Accounts
          </div>
          <div className="max-h-48 overflow-y-auto">
            {linkedCustomers.map((customer) => {
              const key = customer.customerNo || customer.customerId;
              const isActive = key === activeNo;
              return (
                <button
                  key={key}
                  onClick={() => handleSwitch(customer)}
                  disabled={isSwitching}
                  className={`w-full text-left px-3 py-2.5 transition-colors border-b last:border-b-0 ${
                    isActive
                      ? 'bg-primary/5 text-primary font-medium'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  } disabled:opacity-50`}
                >
                  <div className="text-sm truncate">
                    {customer.customerName || key}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 flex items-center gap-1">
                    {key}
                    {isActive && (
                      <span className="text-primary font-medium ml-auto">Active</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSwitcher;
